import { EventData, UserStatus } from '../types/EventData';
import { TruncationLimits } from '../types/TruncationLimits';
import { formatAllDayEventDateTime, formatEventDateTime } from './dateFormatters';
import { truncate, truncateAtSubstring } from '../utils/textTruncate';
import { CalendarEvent } from '../api/types';

const TEAMS_MEETING_DELIMITER = '_________________';
const ONLINE_TAG = 'Online';

export function mapEventsToEventDataItems(
  events: CalendarEvent[],
  truncationLimits: TruncationLimits,
): EventData[] {
  const now = new Date();
  return events.map(event => mapEventToEventData(event, truncationLimits, now));
}

export function mapEventToEventData(
  event: CalendarEvent,
  truncationLimits: TruncationLimits,
  now: Date,
): EventData {
  const { date, time } = event.isAllDay
    ? formatAllDayEventDateTime(event.start, event.end)
    : formatEventDateTime(event.start, event.end);

  return {
    eventId: event.eventId,
    tags: mapToTags(event),
    date,
    time,
    isInThePast: new Date(event.end) < now,
    title: truncate(event.title, truncationLimits.title),
    fullTitle: event.title,
    description: truncate(mapDescription(event.description), truncationLimits.description),
    location: truncate(event.location || 'N/A', truncationLimits.location),
    fullLocation: event.location || 'N/A',
    startTimestamp: new Date(event.start).getTime(),
    eventSeriesId: event.eventSeriesId,
    userStatus: mapToUserStatus(event),
  };
}

function mapToTags(event: CalendarEvent): string[] {
  return event.isOnline ? [...event.categories, ONLINE_TAG] : event.categories;
}

function mapDescription(description?: string) {
  if (!description) {
    return '';
  }
  return truncateAtSubstring(description, TEAMS_MEETING_DELIMITER).trim();
}

function mapToUserStatus(event: CalendarEvent): UserStatus {
  const isInvitationPending = event.isInvitationPending;
  const isNotInvited = !event.isAttendee && !isInvitationPending;
  const hasAccepted =
    event.userResponse === 'accepted' || event.userResponse === 'tentativelyAccepted';
  const hasDeclined = event.userResponse === 'declined';
  const isInvited = event.isAttendee && !hasAccepted && !hasDeclined;

  let statusText = '';
  if (hasAccepted) {
    statusText = 'Accepted';
  } else if (hasDeclined) {
    statusText = 'Declined';
  } else if (isInvited || isInvitationPending) {
    statusText = 'Invited';
  }

  return {
    isNotInvited: isNotInvited,
    hasAccepted: hasAccepted,
    hasDeclined: hasDeclined,
    statusText: statusText,
  };
}
